import classNames from 'classnames';
import reactUpdate from 'immutability-helper';
import PropTypes from 'prop-types';
import React from 'react';

import {SchemaTag, TargetFile, UnaffiliatedSchemaTag} from 'folder-items/FileConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import BioentityLinkTagSearchDropdown from 'metadata/BioentityLinkTagSearchDropdown';
import PartSearchDropdown, {PartSearchDropdownHandle} from 'metadata/PartSearchDropdown';
import {SUMMARIZE_THRESHOLD_DEFAULT} from 'metadata/TagValueViewer';
import ValidEntitiesForFieldPathDropdown from 'metadata/ValidEntitiesForFieldPathDropdown';
import {FIELD_TYPES, getRequiredLinkTagSchema} from 'schemas/fields/SchemaFieldConstants';
import {ValidEntitiesForFieldPathQuery} from 'schemas/tags/__generated/ValidEntitiesForFieldPathQuery';
import {BaseBioentityLinkInputProps} from 'schemas/tags/BioentityLinkInputConstants';
import ControlledSearchInput from 'schemas/tags/ControlledSearchInput';
import * as TagUtil from 'schemas/tags/TagUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import {CustomAttachDropdownFromOptionData} from 'ui-library/attach-dropdown/AttachDropdownOption';
import BioentityChip from 'ui-library/chips/BioentityChip';
import {ChipSize} from 'ui-library/chips/Chip';
import SummaryChip from 'ui-library/chips/SummaryChip';
import * as logging from 'util/Logging';

type ValidEntityGraphQL = NonNullable<
  NonNullable<
    ExtractByTypename<
      ValidEntitiesForFieldPathQuery['bioentity'],
      'BasicFolderItemNode'
    >['validEntitiesForFieldPathInfo']
  >['validEntities']
>[number];

type ControlledBioentityLinkInputProps<T extends SchemaTag | UnaffiliatedSchemaTag> =
  BaseBioentityLinkInputProps<T> & {
    inputValue: string;
    onUpdateInputValue: (inputValue: string) => void;
    validEntities?: Array<ValidEntityGraphQL>;
    loadingValidEntities: boolean;
    summarizeThreshold: number;
  };

interface BioentityChipsProps {
  items: Array<TargetFile>;
  summarize: boolean;
  summarizeThreshold: number;
  chipSize: ChipSize;
  onRemoveItem: (index: number) => void;
  onRemoveAll: () => void;
  areChipsClickable?: boolean;
}

function BioentityChips({
  summarize,
  summarizeThreshold,
  items,
  chipSize,
  onRemoveItem,
  onRemoveAll,
  areChipsClickable,
}: BioentityChipsProps): JSX.Element | null {
  if (items.length === 0) {
    return null;
  }
  if (summarize && items.length >= summarizeThreshold) {
    return (
      <SummaryChip
        size={chipSize}
        itemSummarizer={(targetFile) => targetFile.name}
        itemLabel={FIELD_TYPES.BIOENTITY_LINK.label}
        items={items}
        onRemove={onRemoveAll}
      />
    );
  } else {
    return (
      <>
        {items.map((file, index) => (
          <BioentityChip
            className="chip-container"
            id={file.id}
            isLink={Boolean(areChipsClickable)}
            isInaccessible={!file.id}
            key={index}
            size={chipSize}
            onRemove={() => {
              onRemoveItem(index);
            }}
          />
        ))}
      </>
    );
  }
}

export default function ControlledBioentityLinkInput(
  props: ControlledBioentityLinkInputProps<SchemaTag | UnaffiliatedSchemaTag>
): JSX.Element {
  function _getTargetFiles(): Array<TargetFile> {
    return props.tag.targetFiles || [];
  }

  function _canSelectAdditional(): boolean {
    const {tagSchemaField} = props.tag;
    const targetFiles = _getTargetFiles();
    return targetFiles.length === 0 || tagSchemaField.isMulti || Boolean(props.shouldAllowMultiple);
  }

  function _getLinksAreDisplayed(): boolean {
    return _getTargetFiles().length > 0;
  }

  function _removeLink(index: number): void {
    const targetFiles = reactUpdate(props.tag.targetFiles, {$splice: [[index, 1]]});
    props.onChange({
      targetFiles,
      value: '',
    });
  }

  function _removeAllLinks(): void {
    props.onChange({
      targetFiles: [],
      value: '',
    });
  }

  function _handleUpdateInputValue(inputValue: string): void {
    props.onUpdateInputValue(inputValue);
    props.onChange({
      targetFiles: props.tag.targetFiles,
      value: '',
    });
  }

  function _handleSelectAttachment(id: string, name: string, url: string): void {
    const targetFiles = reactUpdate(_getTargetFiles(), {$push: [{id, name, url}]});
    props.onUpdateInputValue('');
    props.onChange({
      targetFiles,
      value: '',
    });
  }

  function _renderBioentityLinkDropdown(ref: React.RefObject<BioentityLinkTagSearchDropdown>): JSX.Element {
    if (props.validEntities || props.loadingValidEntities) {
      return (
        <ValidEntitiesForFieldPathDropdown
          validEntities={props.validEntities}
          query={props.inputValue}
          onSelectOption={_handleSelectAttachment}
        />
      );
    }
    return (
      <BioentityLinkTagSearchDropdown
        ref={ref}
        query={props.inputValue}
        selectAttachment={_handleSelectAttachment}
        requiredLink={props.tag.tagSchemaField.requiredLink!}
        noResultsItems={props.noResultsOptions?.map(({option, onClick}) => ({
          menuItem: <CustomAttachDropdownFromOptionData optionData={option} />,
          onClick,
        }))}
      />
    );
  }

  function _renderSearchDropdown(
    ref: React.RefObject<BioentityLinkTagSearchDropdown | PartSearchDropdownHandle>
  ): JSX.Element | null {
    if (!props.sequenceParts) {
      // If no parts are given, search all sequences and allow any sequence of the right schema to be
      // selected
      return _renderBioentityLinkDropdown(ref as React.RefObject<BioentityLinkTagSearchDropdown>);
    }
    const fieldType = TagUtil.getFullFieldType(props.tag.tagSchemaField.fieldType);
    const tagSchema = getRequiredLinkTagSchema(props.tag.tagSchemaField);
    switch (fieldType.type) {
      case FIELD_TYPES.BIOENTITY_LINK.type:
      case FIELD_TYPES.TRANSCRIPTION_LINK.type:
      case FIELD_TYPES.TRANSLATION_LINK.type:
      case FIELD_TYPES.AA_PART_LINK.type:
        return _renderBioentityLinkDropdown(ref as React.RefObject<BioentityLinkTagSearchDropdown>);
      case FIELD_TYPES.PART_LINK.type:
        return (
          <PartSearchDropdown
            ref={ref as React.RefObject<PartSearchDropdownHandle>}
            query={props.inputValue}
            selectAttachment={_handleSelectAttachment}
            sequenceParts={props.sequenceParts}
            tagSchemaId={tagSchema ? tagSchema.id : null}
          />
        );
      default:
        logging.error(`Unexpected fieldType in BioentityLinkInput: ${fieldType}`);
        return null;
    }
  }

  const fieldType = TagUtil.getFullFieldType(props.tag.tagSchemaField.fieldType).type;
  const shouldForwardKeydown = fieldType === FIELD_TYPES.PART_LINK.type || Boolean(props.inputValue);

  return (
    <div
      className={classNames(props.className, 'bioentityLinkInput')}
      {...getTestProps({
        component: 'ControlledBioentityLinkInput',
        element: 'root',
      })}
    >
      <ControlledSearchInput
        isDisabled={props.isDisabled}
        shouldForwardKeydown={shouldForwardKeydown}
        canSelectAdditional={_canSelectAdditional()}
        hasTypedValue={_getLinksAreDisplayed()}
        onRemoveLastValue={() => {
          _removeLink(_getTargetFiles().length - 1);
        }}
        autoFocus={Boolean(props.autoFocus)}
        inputValue={props.inputValue}
        onUpdateInputValue={_handleUpdateInputValue}
        onKeyDown={props.onKeyDown}
        onBlur={props.onBlur}
        renderSelectedValues={() => (
          <BioentityChips
            items={_getTargetFiles()}
            // Inside an assay run with automation input file configs, we summarize the links
            summarize={props.summarize}
            summarizeThreshold={props.summarizeThreshold}
            chipSize={props.chipSize}
            onRemoveItem={_removeLink}
            onRemoveAll={_removeAllLinks}
            areChipsClickable={props.areChipsClickable}
          />
        )}
        renderSearchDropdown={_renderSearchDropdown}
        shouldCloseOnInsideClick={true}
        isOutsideTable={props.isOutsideTable}
        placeholder={_canSelectAdditional() ? props.placeholder : ''}
      />
    </div>
  );
}

ControlledBioentityLinkInput.defaultProps = {
  autoFocus: true,
  chipSize: ChipSize.SMALL,
  shouldAllowMultiple: false,
  summarize: false,
  summarizeThreshold: SUMMARIZE_THRESHOLD_DEFAULT,
  onAddSamplesMenuItemClick: null,
  loadingValidEntities: false,
  isDisabled: false,
};

ControlledBioentityLinkInput.propTypes = {
  chipSize: PropTypes.oneOf(Object.values(ChipSize)).isRequired,
  autoFocus: PropTypes.bool.isRequired,
  className: PropTypes.string,
  inputValue: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onUpdateInputValue: PropTypes.func.isRequired,
  // Allows the parent component to catch keydown events, e.g. pressing Enter to save
  onKeyDown: PropTypes.func,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string.isRequired,
  sequenceParts: PropTypes.array,
  searchParams: PropTypes.object,
  shouldAllowMultiple: PropTypes.bool.isRequired,
  areChipsClickable: PropTypes.bool,
  tag: PropTypes.object.isRequired,
  isDisabled: PropTypes.bool,
};
